import React, { useEffect, useState } from "react";
import { AddButton, ConfirmModal, Modal, Tag } from "components";
import get from "lodash/get";
import { helpers } from "services";
import EntityContainer from "modules/entity/containers";
import Create from "pages/Warehouse/List/create";
import Update from "pages/Warehouse/List/update";
import ModulesActions from "modules/entity/actions";
import { useDispatch, useSelector } from "react-redux";
import useAlert from "hooks/useAlert";
import StoreActions from "store/actions/system";
import useAccess from "hooks/useAccess";
import { ReactComponent as EditIcon } from "assets/images/icons/pen.svg?react";
import { ReactComponent as DeleteIcon } from "assets/images/icons/trash.svg?react";
import { Link } from "react-router-dom";
import useTranslation from "hooks/useTranslation";

const SelfLeftovers = () => {
	const { tr } = useTranslation();

	const hasAccessEdit = useAccess({ roles: ["manager", "owner"] });
	const default_currency_code = useSelector(
		state => state.auth.default_currency_code
	);
	const currency = default_currency_code && default_currency_code.toUpperCase();

	const dispatch = useDispatch();
	const { showAlert } = useAlert();

	const [createModal, showCreateModal] = useState(false);
	const [updateModal, showUpdateModal] = useState(false);
	const [deleteModal, showDeleteModal] = useState(false);
	const [selected, setSelected] = useState(null);
	const [isSubmitting, setSubmitting] = useState(false);
	const [reports, setReports] = useState(null);

	const openDeleteModal = item => {
		setSelected(item);
		showDeleteModal(true);
	};

	const deleteAction = () => {
		const selectedId = get(selected, "id");
		setSubmitting(true);
		dispatch(
			ModulesActions.Form.request({
				method: "delete",
				entity: "storages",
				name: `all`,
				deleteData: true,
				id: selectedId,
				url: `/storages/${selectedId}`,
				cb: {
					success: () => {
						showAlert(tr("Успешно удалено"), { type: "success" });
						showDeleteModal(false);
						setSelected(null);
					},
					error: response => {
						const errorMessage = get(response, "errorMessage", "");
						showAlert(errorMessage, { type: "danger" });
					},
					finally: () => {
						setSubmitting(false);
					}
				}
			})
		);
	};

	useEffect(() => {
		dispatch(StoreActions.SetToolbarTitle(tr("Остаток")));
	}, []);

	const loadStorageReports = () => {
		dispatch(
			ModulesActions.LoadDefault.request({
				url: "/reports/storages",
				cb: {
					success: data => {
						setReports(data);
					},
					error: data => { }
				}
			})
		);
	};

	useEffect(() => {
		loadStorageReports();
	}, []);

	return (
		<div className="d-panel">
			<Modal
				size={500}
				isOpen={createModal}
				toggle={() => showCreateModal(false)}
			>
				<Create {...{ showCreateModal }} />
			</Modal>
			<Modal
				size={500}
				isOpen={updateModal}
				toggle={() => showUpdateModal(false)}
			>
				<Update {...{ showUpdateModal, selected }} />
			</Modal>
			<Modal
				size={400}
				isOpen={deleteModal}
				toggle={() => showDeleteModal(false)}
				closeIcon={false}
			>
				<ConfirmModal
					text={`Вы точно хотите удалить?`}
					onConfirmHandler={deleteAction}
					onCancelHandler={() => showDeleteModal(false)}
					isSubmitting={isSubmitting}
				/>
			</Modal>

			<div className="d-flex align-items-center justify-content-between mb-10">
				<div className="main-title">{tr("Остаток главной организации")}</div>
				<AddButton
					title={tr("Добавить")}
					handleClick={() => showCreateModal(true)}
				/>
			</div>
			<EntityContainer.All
				entity="storages"
				name={`all`}
				url="/storages"
				primaryKey="id"
				params={{
					sort: "id",
					limit: 100
				}}
			>
				{({ items, isFetched, meta }) => {
					return (
						<div>
							<table className="default-table default-table-hover">
								<thead>
									<tr>
										<th className="text-cen">{tr("№")}</th>
										<th>{tr("Название")}</th>

										<th className="text-right">{tr("Сырьё")}</th>
										<th className="text-right">{tr("Полуфабрикат")}</th>
										<th className="text-right">{tr("Реализация")}</th>

										<th className="text-right">{tr("Сумма")}</th>
										<th className="text-cen">{tr("Статус")}</th>
										<th className="text-cen"></th>
									</tr>
								</thead>
								<tbody>
									<tr>
										<td className="bg-main"></td>
										<td className="fw-600 bg-main">{tr("Итого")}</td>
										<td className="text-right fw-600 bg-main">
											{helpers.convertToReadable(
												get(reports, `total[Сыря]`, 0)
											)}{" "}
											{currency}
										</td>
										<td className="text-right fw-600 bg-main">
											{helpers.convertToReadable(
												get(reports, `total[Полуфабрикат]`, 0)
											)}{" "}
											{currency}
										</td>
										<td className="text-right fw-600 bg-main">
											{helpers.convertToReadable(
												get(reports, `total[Реализация]`, 0)
											)}{" "}
											{currency}
										</td>
										<td className="text-right fw-600 bg-main">
											{helpers.convertToReadable(
												get(reports, `total[Реализация]`, 0) +
												get(reports, `total[Сыря]`, 0) +
												get(reports, `total[Полуфабрикат]`, 0)
											)}
											{currency}
										</td>
										<td className="bg-main"></td>
										<td className="bg-main"></td>
									</tr>
									{items.map((item, i) => {
										const reportItem = reports && reports[item.name];

										const material = reportItem && reportItem["Сыря"];
										const product = reportItem && reportItem["Реализация"];
										const semiFinished =
											reportItem && reportItem["Полуфабрикат"];

										const materialSum = Number(get(material, "price", 0));
										const semiFinishedSum = Number(
											get(semiFinished, "price", 0)
										);
										const productSum = Number(get(product, "price", 0));
										const totalSum = materialSum + semiFinishedSum + productSum;

										return (
											<tr>
												<td className="w-50 text-cen">{i + 1}</td>
												<td>
													<Link
														to={`/storages/${item.id}`}
														className="text-link-style fw-600 cursor-pointer"
													>
														{item.name}
													</Link>
												</td>
												<td
													className={`text-right ${materialSum ? "fw-500" : "fo-5"
														}`}
												>
													{helpers.convertToReadable(materialSum)} {currency}
												</td>
												<td
													className={`text-right ${semiFinishedSum ? "fw-500" : "fo-5"
														}`}
												>
													{helpers.convertToReadable(semiFinishedSum)}{" "}
													{currency}
												</td>
												<td
													className={`text-right ${productSum ? "fw-500" : "fo-5"
														}`}
												>
													{helpers.convertToReadable(productSum)} {currency}
												</td>
												<td className={`text-right bg-main`}>
													<div className={`${totalSum ? "fw-500" : "fo-5"}`}>
														{helpers.convertToReadable(totalSum)} {currency}
													</div>
												</td>
												<td className="w-120 text-cen">
													<Tag color={helpers.getStatus(item.status, tr).color}>
														{helpers.getStatus(item.status, tr).label}
													</Tag>
												</td>
												<td className="w-100">
													{hasAccessEdit && (
														<div className="d-flex justify-content-center">
															{item.is_default === 0 ? (
																<>
																	<span
																		className="action-btn action-btn--info"
																		onClick={() => {
																			setSelected(item);
																			showUpdateModal(true);
																		}}
																	>
																		<EditIcon />
																	</span>

																	<span
																		className="action-btn action-btn--danger"
																		onClick={() => {
																			openDeleteModal(item);
																		}}
																	>
																		<DeleteIcon />
																	</span>
																</>
															) : null}
														</div>
													)}
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					);
				}}
			</EntityContainer.All>
		</div>
	);
};

export default SelfLeftovers;
